/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import Modal from '@mui/material/Modal';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import TableFooter from '@mui/material/TableFooter';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';

import { formatDate } from '../sharedcomponents/utils';
import { selectCMEDocuments, selectCMEState, setActiveStep } from './cmeSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import styles from './CMETable.module.scss';
import DocumentName from '../sharedcomponents/DocumentName/DocumentName';
import { CMETableHeadProps, CMECreditsPropsType, CMEDataType, HeadCell } from './cmeTypes';
import { CME_DATE_FORMAT } from '../sharedcomponents/constants';
import { PEARL_PUBLIC_URL } from '../sharedcomponents/api/api_config';
import { useStateLocalStorage } from '../sharedcomponents/useHooks';

const cmeHeadCells: readonly HeadCell[] = [
  {
    id: 'title',
    label: 'Topic',
    align: 'left',
    sort: true,
  },
  {
    id: 'qualified_on',
    label: 'Qualified on',
    align: 'left',
    sort: true,
  },
];

function CMECredits({ credits, selected }: CMECreditsPropsType) {
  return <span className={!selected ? styles.muted : ''}>{credits?.toFixed(2)}</span>;
}

function CMETableHead({
  order,
  orderBy,
  handleSort,
  selectedRows,
  cmeList,
  onSelectAllClick,
  totalCredits,
}: CMETableHeadProps) {
  const numSelected = selectedRows.length;
  const rowCount = cmeList.length;

  return (
    <TableHead>
      <TableRow>
        {cmeHeadCells.map((cell) => (
          <TableCell
            key={cell.id}
            align={cell.align}
            sortDirection={orderBy === cell.id ? order : false}
            className={styles.tableHeader}
          >
            {cell.sort ? (
              <TableSortLabel
                active={orderBy === cell.id}
                direction={orderBy === cell.id ? order : 'asc'}
                onClick={() => handleSort(cell.id)}
              >
                {cell.label}
                {orderBy === cell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              cell.label
            )}
          </TableCell>
        ))}
        <TableCell align="right" className={styles.selectAllTableCell}>
          <FormControlLabel
            label={
              <>
                <span className={styles.selectAll}>
                  Select all <small>({rowCount}) </small>
                </span>
                <CMECredits credits={totalCredits} selected={numSelected === rowCount && numSelected > 0} />
              </>
            }
            sx={{ m: 0 }}
            labelPlacement="start"
            control={
              <Checkbox
                color="green"
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{
                  'aria-label': 'select all documents',
                }}
              />
            }
          />
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

export default function CMETable() {
  const { cmeList, fetchingCMEList } = useAppSelector(selectCMEState);
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState<CMEDataType[]>([]);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<keyof CMEDataType>('qualified_on');
  const [rowsPerPage, setRowsPerPage] = useStateLocalStorage('cme.pageSize', 25);
  const [learnerNotification, setLearnerNotification] = useState(false);

  const { unclaimed } = useAppSelector(selectCMEState);

  const handleSort = (sortBy: keyof CMEDataType) => {
    if (sortBy !== orderBy) {
      setOrderBy(sortBy);
      setOrder('asc');
    } else {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    }
  };

  const isSelected = (id: string) => selectedRows.some((row: CMEDataType) => row.id === id);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleClick = (event: React.MouseEvent<unknown>, clickedRow: CMEDataType) => {
    const selectedIndex = selectedRows.map(({ id }) => id).indexOf(clickedRow.id);
    let newSelected: CMEDataType[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, clickedRow);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selectedRows.slice(0, selectedIndex), selectedRows.slice(selectedIndex + 1));
    }

    setSelectedRows(newSelected);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = cmeList.map((n) => n);
      setSelectedRows(newSelected);
      return;
    }
    setSelectedRows([]);
  };

  const goToQuestionnaire = () => {
    dispatch(selectCMEDocuments(selectedRows));
    dispatch(setActiveStep('questionnaire'));
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - cmeList.length) : 0;

  const visibleRows = useMemo(() => {
    const sortedList = [...cmeList]?.sort(
      (a: CMEDataType, b: CMEDataType) => ((a[orderBy] || 0) > (b[orderBy] || 0) ? 1 : -1) * (order === 'asc' ? 1 : -1)
    );
    return sortedList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [page, rowsPerPage, order, orderBy, cmeList]);

  return (
    <Box sx={{ width: '100%', pl: 1, pr: 1 }}>
      <TableContainer data-testid="cme-table" className={styles.claimableTableContainer}>
        <Table stickyHeader size="small" aria-labelledby="cme-table">
          <CMETableHead
            order={order}
            orderBy={orderBy}
            handleSort={handleSort}
            selectedRows={selectedRows}
            onSelectAllClick={handleSelectAllClick}
            cmeList={cmeList}
            totalCredits={unclaimed}
          />
          <TableBody>
            {visibleRows.map((row) => {
              const isItemSelected = isSelected(row.id);
              return (
                <TableRow tabIndex={-1} key={row.id} hover role="checkbox" aria-checked={isItemSelected}>
                  <TableCell className={styles.cells}>
                    <DocumentName title={row.title} />
                  </TableCell>
                  <TableCell id={row.qualified_on} scope="row" className={styles.qualifiedOnTableCell}>
                    {formatDate(row.qualified_on, CME_DATE_FORMAT)}
                  </TableCell>
                  <TableCell align="right" className={styles.cells}>
                    <FormControlLabel
                      label={<CMECredits credits={Number(row.credits)} selected={isItemSelected} />}
                      sx={{ m: 0 }}
                      labelPlacement="start"
                      control={
                        <Checkbox
                          onClick={(event) => handleClick(event, row)}
                          color="green"
                          checked={isItemSelected}
                          inputProps={{
                            'aria-labelledby': row.title,
                          }}
                        />
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={3} />
              </TableRow>
            )}
            {fetchingCMEList && (
              <TableRow>
                <TableCell align="center" colSpan={3}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {!cmeList.length && !fetchingCMEList && (
            <TableFooter>
              <TableRow>
                <TableCell className={styles.tableHeader} colSpan={3} align="center">
                  No claimable documents available
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[8, 15, 25]}
        component="div"
        count={cmeList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        className={styles.pagination}
      />
      <Stack sx={{ pb: 1 }} direction="row" justifyContent="space-between" flexWrap="wrap-reverse" rowGap={2}>
        <Button color="green" onClick={() => setLearnerNotification(true)} variant="contained">
          Required learner notification
        </Button>
        <Button color="green" onClick={goToQuestionnaire} disabled={!selectedRows.length} variant="contained">
          Click here to initiate CME certification
        </Button>
      </Stack>
      <Modal
        className={styles.modal}
        open={learnerNotification}
        onClose={() => setLearnerNotification(false)}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <Box className={styles.modalContent}>
          <Card sx={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'scroll' }}>
            <CardMedia
              component="img"
              image={`${PEARL_PUBLIC_URL}/CMELearnerNotification.png`}
              alt="CME Learner Notification"
            />
          </Card>
          <IconButton
            className={styles.closeIcon}
            aria-label="close learner notification"
            onClick={() => setLearnerNotification(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Modal>
    </Box>
  );
}
